<template>
  <div>
    <div ref="rowRef" class="mt-3 flex items-center gap-2 overflow-hidden">
      <span class="mr-1 shrink-0 text-xs font-medium text-gray-60">{{
        $t('conversations.filters.ai_search.example_label')
      }}</span>
      <ui-tag
        v-for="(suggestion, index) in candidates"
        :key="suggestion.key"
        data-suggestion-chip
        theme="zennovate"
        tag="button"
        :class="{ hidden: index >= visibleCount }"
        @click="useSuggestion(suggestion.label)"
      >
        {{ suggestion.label }}
      </ui-tag>
      <button
        ref="refreshRef"
        type="button"
        :aria-label="$t('conversations.filters.ai_search.refresh_button')"
        :title="$t('conversations.filters.ai_search.refresh_button')"
        class="ml-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-gray-50 transition-colors hover:bg-gray-10 hover:text-gray-70"
        @click="shuffle"
      >
        <refresh-cw-icon class="icon-s" />
      </button>
    </div>
    <div v-if="isLoading" class="mt-8 flex items-center justify-center gap-2 p-2">
      <ui-spinner inline no-spinner-background spinner-color="blue-40" />
      <span class="text-xs text-gray-70">{{ $t(loadingStepLabelKey) }}</span>
    </div>
    <ui-alert
      v-else-if="!explanationDismissed && assistantResponse"
      compressed
      full-width
      closable
      class="mt-8"
      @close="explanationDismissed = true"
    >
      {{ assistantResponse }}
    </ui-alert>
    <div
      v-else-if="isFetched && hasSubmitted && !lastResultCount"
      class="mt-8 flex items-center justify-center p-2 text-xs text-gray-70"
    >
      {{ $t('conversations.filters.ai_search.no_results') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, useTemplateRef } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { UiSpinner, UiTag, UiAlert } from '@klausapp/ui-kit'
import { RefreshCwIcon } from '@klausapp/ui-kit/icons'
import { session } from '@/composables/useSession'

import { deconstructFilterOptions, type DeconstructedOption } from '../../utils/filters'
import { type DefaultFilterOption } from '../../api/filters'
import { getAiFilterOptions } from '../../api/ai-filters'
import { usePromptSuggestions } from './usePromptSuggestions'
import { useProgressiveLoading } from './useProgressiveLoading'

const props = defineProps<{
  options: DefaultFilterOption[]
  autoqaFields: DefaultFilterOption[]
  spotlightFields: DefaultFilterOption[]
  intelligentFields: DefaultFilterOption[]
  prompt: string
}>()

const emit = defineEmits<{
  (e: 'ai-results', value: DeconstructedOption[]): void
  (e: 'use-suggestion', value: string): void
  (e: 'loading', value: boolean): void
}>()

const explanationDismissed = ref(false)
const hasSubmitted = ref(false)
const lastResultCount = ref(0)
const assistantResponse = ref('')
const submittedPrompt = ref('')

const rowRef = useTemplateRef<HTMLElement>('rowRef')
const refreshRef = useTemplateRef<HTMLElement>('refreshRef')
const { candidates, visibleCount, shuffle } = usePromptSuggestions(rowRef, refreshRef)

const queryClient = useQueryClient()
const queryKey = ['ai-filter-options', session.workspace.id, submittedPrompt]

const {
  refetch,
  isFetching: isLoading,
  isFetched,
} = useQuery({
  queryKey,
  // TanStack passes an AbortSignal that it aborts on cancelQueries — forward it so the ML request
  // is actually cancelled (not just its result ignored) when a new prompt supersedes it.
  queryFn: ({ signal }) => getAiFilterOptions(submittedPrompt.value, signal),
  enabled: false,
  refetchOnMount: false,
})

// Progressive loading message that advances on fixed timers while the search runs.
const { loadingStepLabelKey, start: startProgressiveLoading, stop: clearLoadingTimers } = useProgressiveLoading()

const useSuggestion = (suggestion: string) => {
  // reflect the clicked suggestion in the input, then run it
  emit('use-suggestion', suggestion)
  submitWithPrompt(suggestion)
}

const submit = () => submitWithPrompt(props.prompt)

// Identifies the latest submit so a superseded (cancelled) request bails out after it resolves.
let latestRequestId = 0

async function submitWithPrompt(prompt: string) {
  // Allow re-submitting while loading as long as the prompt changed; block only identical re-submits.
  if (!prompt || (isLoading.value && prompt === submittedPrompt.value)) return

  const requestId = ++latestRequestId

  // Cancel any in-flight request so its network call is aborted and its result can't overwrite the new one.
  await queryClient.cancelQueries({ queryKey })

  // Another submit started during the await above — let it own the state and bail out of this one.
  if (requestId !== latestRequestId) return

  submittedPrompt.value = prompt
  hasSubmitted.value = true
  explanationDismissed.value = false
  startProgressiveLoading()
  // Emit synchronously so the parent can reset/hide filter options before the request resolves
  emit('loading', true)

  const { data: response, error } = await refetch()

  // A newer submit superseded this one — drop the (cancelled) result.
  if (requestId !== latestRequestId) return

  clearLoadingTimers()

  if (error || !response) {
    console.error('Error fetching AI filter options', error)
    emit('loading', false)
    return
  }

  const { options, autoQaOptions } = response

  // The ai-options endpoint returns every non-autoQa option inside `options` with type DEFAULT,
  // including AutoQA and Intelligent categories. Re-route them by name into the correct buckets so
  // deconstructFilterOptions flags them (isAutoQa/isIntelligent) and they land in the right payload
  // array — otherwise the backend rejects them as invalid default options.
  const autoQaNames = new Set((props.autoqaFields || []).map((f) => f.filterOption))
  const intelligentNames = new Set((props.intelligentFields || []).map((f) => f.filterOption))

  const returnedOptions = options || []
  const defaultOptions = returnedOptions.filter((o) => !autoQaNames.has(o.option) && !intelligentNames.has(o.option))
  const misplacedAutoQaOptions = returnedOptions.filter((o) => autoQaNames.has(o.option))
  const intelligentOptions = returnedOptions.filter((o) => intelligentNames.has(o.option))

  const results = deconstructFilterOptions({
    options: defaultOptions,
    autoQaOptions: [...(autoQaOptions || []), ...misplacedAutoQaOptions],
    spotlightOptions: [],
    customCategoryOptions: [],
    intelligentFilterOptions: intelligentOptions,
    defaultOptions: props.options || [],
    defaultAutoQaOptions: props.autoqaFields || [],
    defaultSpotlightOptions: props.spotlightFields || [],
    defaultIntelligentFilterOptions: props.intelligentFields || [],
  })

  lastResultCount.value = results.length
  assistantResponse.value = response.assistantResponse || ''
  emit('ai-results', results)
  emit('loading', false)
}

defineExpose({ submit })
</script>
